import React from 'react';
import {
  Box, Container, Typography, Grid, Card, CardMedia,
  CardContent, CardActionArea, Chip, Stack
} from '@mui/material';
import { blueGrey, lightBlue, grey } from '@mui/material/colors';
import { motion } from 'framer-motion';

function ScrollReveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

const CAMPAIGNS = [
  {
    title: '탄소 발자국 줄이기 캠페인',
    description: '2024년 다끌레어와 함께한 탄소 발자국 감축 캠페인. 전국 500여 개 기업이 참여하여 총 1,200톤의 탄소를 절감했습니다.',
    date: '2024. 05.',
    tag: '환경 캠페인',
    tagColor: lightBlue[700],
    image: '/assets/images/news/env_back3.jpg',
    status: 'done',
  },
  {
    title: '혁신장터 청소카트 특별 프로모션',
    description: '공공기관 대상 다끌레어 청소카트 혁신장터 특별 판매 프로모션. 혁신제품 지정 기념 할인 행사입니다.',
    date: '2024. 08.',
    tag: '제품 프로모션',
    tagColor: blueGrey[600],
    image: '/assets/images/news/installation_image.jpg',
    status: 'done',
  },
  {
    title: '탄소중립마켓(VCM) 론칭 이벤트',
    description: '다끌레어 VCM 플랫폼 론칭을 기념하여 탄소 크레딧 첫 거래 기업에 특별 혜택을 제공합니다.',
    date: '2025. 03.',
    tag: 'VCM',
    tagColor: '#006064',
    image: '/assets/images/news/VCM_image.jpg',
    status: 'upcoming',
  },
  {
    title: '환경의 날 기념 ESG 세미나',
    description: '6월 5일 환경의 날을 맞아 ESG 경영 전략과 탄소 중립 실천 방안을 주제로 세미나를 개최할 예정입니다.',
    date: '2025. 06.',
    tag: '세미나',
    tagColor: blueGrey[700],
    image: '/assets/images/news/exhibition_image.jpg',
    status: 'upcoming',
  },
];

export default function CampaignsPage() {
  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#f4f6f8' }}>
      {/* 헤더 히어로 */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 50%, #0d2137 100%)',
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 배경 그리드 패턴 */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(33,150,243,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(33,150,243,0.05) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label="고객지원"
                size="small"
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '1px',
                  backgroundColor: 'rgba(33,150,243,0.15)',
                  color: '#90caf9',
                  border: '1px solid rgba(33,150,243,0.3)',
                  mb: 3,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 900,
                  fontSize: { xs: '32px', md: '52px' },
                  color: '#ffffff',
                  letterSpacing: { xs: '-0.5px', md: '-1.5px' },
                  mb: 2,
                  lineHeight: 1.15,
                }}
              >
                캠페인
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 300,
                  fontSize: { xs: '15px', md: '17px' },
                  color: 'rgba(255,255,255,0.55)',
                  lineHeight: 1.8,
                }}
              >
                다끌레어의 환경 캠페인과 프로모션 소식을 확인하세요
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* 캠페인 카드 목록 */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Grid container spacing={3}>
          {CAMPAIGNS.map((campaign, i) => (
            <Grid size={{ xs: 12, sm: 6 }} key={campaign.title}>
              <ScrollReveal delay={i * 0.1}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: '20px',
                    border: `1px solid ${grey[200]}`,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 16px 48px rgba(0,0,0,0.1)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <CardActionArea>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="div"
                        sx={{
                          height: 200,
                          backgroundColor: blueGrey[100],
                          backgroundImage: `url(${campaign.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      {/* 상태 배지 */}
                      <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
                        <Chip
                          label={campaign.status === 'upcoming' ? '예정' : '완료'}
                          size="small"
                          sx={{
                            fontFamily: 'NotoSansKR',
                            fontWeight: 700,
                            fontSize: '11px',
                            backgroundColor: campaign.status === 'upcoming' ? lightBlue[600] : grey[600],
                            color: '#fff',
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ px: 3, py: 3 }}>
                      <Stack spacing={1.5}>
                        <Chip
                          label={campaign.tag}
                          size="small"
                          sx={{
                            fontFamily: 'NotoSansKR',
                            fontWeight: 600,
                            fontSize: '11px',
                            backgroundColor: campaign.tagColor,
                            color: '#fff',
                            alignSelf: 'flex-start',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'NotoSansKR',
                            fontWeight: 700,
                            fontSize: '17px',
                            textAlign: 'left',
                            color: blueGrey[900],
                            lineHeight: 1.4,
                          }}
                        >
                          {campaign.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'NotoSansKR',
                            fontWeight: 400,
                            fontSize: '13px',
                            color: blueGrey[600],
                            textAlign: 'left',
                            lineHeight: 1.75,
                          }}
                        >
                          {campaign.description}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'NotoSansKR',
                            fontWeight: 500,
                            fontSize: '12px',
                            textAlign: 'left',
                            color: grey[500],
                          }}
                        >
                          {campaign.date}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </ScrollReveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
